
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, FileText, AlertCircle } from "lucide-react";

interface DocumentMetadata {
  title: string;
  category: string;
  description?: string;
}

interface DocumentUploadFormProps {
  onUpload: (file: File, metadata: DocumentMetadata) => void;
  isUploading: boolean;
  onCancel: () => void;
}

export const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  onUpload,
  isUploading,
  onCancel
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<DocumentMetadata>({
    title: '',
    category: 'general',
    description: ''
  });
  const [error, setError] = useState<string>('');

  const supportedFileTypes = [
    '.pdf', '.txt', '.md', '.csv', '.docx'
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setError('');
    
    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Validate file type
    const fileName = selectedFile.name.toLowerCase();
    const isSupported = supportedFileTypes.some(type => fileName.endsWith(type));
    
    if (!isSupported) {
      setError(`Unsupported file type. Supported formats: ${supportedFileTypes.join(', ')}`);
      setFile(null);
      return;
    }

    // Validate file size (50MB limit)
    if (selectedFile.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    
    // Auto-fill title if empty
    if (!metadata.title) {
      const nameWithoutExtension = selectedFile.name.replace(/\.[^/.]+$/, '');
      setMetadata(prev => ({ ...prev, title: nameWithoutExtension }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file');
      return;
    }
    
    if (!metadata.title.trim()) {
      setError('Please enter a title');
      return;
    }
    
    onUpload(file, metadata);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="file">Document File</Label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <Input
            id="file"
            type="file"
            accept={supportedFileTypes.join(',')}
            onChange={handleFileChange}
            className="hidden"
          />
          <Label htmlFor="file" className="cursor-pointer">
            <div className="flex flex-col items-center space-y-2">
              {file ? (
                <>
                  <FileText className="h-12 w-12 text-blue-500" />
                  <div className="text-sm">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </>
              ) : (
                <>
                  <Upload className="h-12 w-12 text-gray-400" />
                  <div className="text-sm">
                    <p className="font-medium">Click to upload or drag and drop</p>
                    <p className="text-gray-500">
                      Supported: {supportedFileTypes.join(', ')} (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>
          </Label>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={metadata.title}
          onChange={(e) => setMetadata(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Enter document title"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category</Label>
        <Select 
          value={metadata.category} 
          onValueChange={(value) => setMetadata(prev => ({ ...prev, category: value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nav-rapporter">NAV Rapporter</SelectItem>
            <SelectItem value="arbeidsmarked">Arbeidsmarked</SelectItem>
            <SelectItem value="utdanning">Utdanning</SelectItem>
            <SelectItem value="karriere">Karriere</SelectItem>
            <SelectItem value="kompetanse">Kompetanse</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description (Optional)</Label>
        <Textarea
          id="description"
          value={metadata.description}
          onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Brief description of the document content"
          rows={3}
        />
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The document will be processed automatically and split into chunks for semantic search. 
          Text files (.txt, .md) and CSV files are processed most reliably.
        </AlertDescription>
      </Alert>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isUploading}>
          Cancel
        </Button>
        <Button type="submit" disabled={!file || isUploading}>
          {isUploading ? 'Processing...' : 'Upload Document'}
        </Button>
      </div>
    </form>
  );
};
